import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminEndpoints } from "@/lib/adminApi";
import {
    AdminPageHeader,
    AdminCard,
    EmptyState,
    StatusPill,
} from "@/components/admin/AdminUI";
import { ArrowUpRight } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

const STATUSES = ["all", "new", "shortlisted", "rejected"];

export default function AdminApplicationsPage() {
    const [applications, setApplications] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [jobFilter, setJobFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            adminEndpoints.applications({}),
            adminEndpoints.jobs(),
        ])
            .then(([a, j]) => {
                if (cancelled) return;
                setApplications(a);
                setJobs(j);
            })
            .catch((err) => {
                if (!cancelled)
                    setError(err?.response?.data?.detail || "Failed to load applications");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const filtered = useMemo(() => {
        return applications.filter((a) => {
            if (jobFilter !== "all" && a.job_id !== jobFilter) return false;
            if (statusFilter !== "all" && a.status !== statusFilter) return false;
            return true;
        });
    }, [applications, jobFilter, statusFilter]);

    return (
        <div data-testid="admin-applications-page">
            <AdminPageHeader
                eyebrow="Careers pipeline"
                title="Applications"
                description="All applications received via /careers. Filter by job or status."
                testId="admin-applications-header"
            />

            <div
                className="mb-6 flex flex-wrap items-center gap-3"
                data-testid="admin-applications-filters"
            >
                <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70">
                    Job
                    <select
                        value={jobFilter}
                        onChange={(e) => setJobFilter(e.target.value)}
                        className="border border-white/10 bg-white/[0.03] px-3 py-2 font-body text-sm text-brand-offwhite focus:border-brand-gold focus:outline-none"
                        data-testid="admin-applications-job-filter"
                    >
                        <option value="all" className="bg-brand-charcoal">
                            All jobs
                        </option>
                        {jobs.map((j) => (
                            <option key={j.id} value={j.id} className="bg-brand-charcoal">
                                {j.title}
                            </option>
                        ))}
                    </select>
                </label>
                <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setStatusFilter(s)}
                            className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
                                statusFilter === s
                                    ? "border-brand-gold bg-brand-gold text-brand-charcoal"
                                    : "border-white/20 text-brand-offwhite/70 hover:border-brand-gold hover:text-brand-gold"
                            }`}
                            data-testid={`admin-applications-status-${s}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}

            {loading ? (
                <AdminCard>Loading…</AdminCard>
            ) : filtered.length === 0 ? (
                <EmptyState
                    title="No applications match those filters"
                    hint="Try clearing filters or wait for applicants to submit."
                    testId="admin-applications-empty"
                />
            ) : (
                <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
                    <table className="min-w-full text-left text-sm" data-testid="admin-applications-table">
                        <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                            <tr>
                                <th className="px-4 py-3">Ref</th>
                                <th className="px-4 py-3">Applicant</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Job</th>
                                <th className="px-4 py-3">Right to work</th>
                                <th className="px-4 py-3">Submitted</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((a) => (
                                <tr
                                    key={a.id}
                                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                                    data-testid={`admin-application-row-${a.id}`}
                                >
                                    <td className="px-4 py-3 font-mono text-xs text-brand-gold">
                                        {a.reference_number}
                                    </td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite">{a.full_name}</td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/80">
                                        {a.email}
                                    </td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                        {a.job_title_snapshot}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/70">
                                        {a.right_to_work}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                        {formatDate(a.submitted_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <StatusPill status={a.status} />
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <Link
                                            to={`/admin/applications/${a.id}`}
                                            className="inline-flex items-center gap-1 border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                                            data-testid={`admin-application-open-${a.id}`}
                                        >
                                            Open
                                            <ArrowUpRight className="h-3.5 w-3.5" />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
